/**
 * Keyed store for the checkout's shipping quote and sales-tax lookup.
 *
 * Jake, 2026-10-22: calculate once before address verification and at most
 * once after it, store the answer, and use the stored answer on the order
 * instead of asking again when the shopper enters a payment method.
 *
 * The cart asks for these figures from many places (the address watcher,
 * Continue to Checkout, the AVS answer, a silent store refresh). Rather than
 * thread "has this already been asked?" through every caller, each lookup is
 * keyed on exactly the inputs its REQUEST depends on, and the memo answers a
 * repeated key from the store. Only a changed input reaches the network.
 *
 * What a key holds -- and deliberately does not:
 *   - destination: country, state, zip (the 5-digit zip for the US, so the
 *     ZIP+4 that address verification appends is the same place). NOT street,
 *     city or suite: AVS rewrites those on almost every address, and the
 *     quote is keyed on zip / state / country (Jake: "if AVS changes the zip
 *     code we should recalculate").
 *   - shipping: the store, the destination, the cart's total weight (the
 *     cart's only input to the rate request) and the store's carrier /
 *     service settings.
 *   - tax: the store, the destination and the cart total the lookup sends.
 *   - NOT the buyer (``user_id``). The quote endpoint uses it only to
 *     attribute an event, not to price, and each memo belongs to ONE cart
 *     component instance (``quoteMemoFor``), so a stored answer cannot reach
 *     another buyer.
 *
 * DISPLAY AND SUBMIT ONLY -- this is not a trust boundary. The server
 * recomputes tax from the ship-to on every order and bounds the posted
 * shipping line (api/src/models/account.py ``_validate_checkout_totals``);
 * nothing stored here lets a client set its own price.
 *
 * Failures, and answers the caller marks uncacheable (a "no rate" envelope),
 * are never stored, so a retry always reaches the network. A stored answer
 * older than ``maxAgeMs`` is fetched again, so a tab left open does not
 * check out on an hours-old carrier rate.
 *
 * The stored value is the caller's WHOLE answer, kept opaque (for shipping,
 * the full response). A later server-side quote record (Jake approved,
 * 2026-10-22, as a separate unit: the quote endpoint saves a row and
 * returns its id, checkout sends the id) needs no change here -- the id
 * rides along inside the stored answer and the caller reads it back out.
 */

/** How long a stored quote may be reused before it is asked again. */
export const QUOTE_MAX_AGE_MS = 15 * 60 * 1000;

const US_NAMES = new Set(["US", "USA", "UNITED STATES", "UNITED STATES OF AMERICA"]);

function clean(value) {
  return value == null ? "" : String(value).trim().toUpperCase();
}

/**
 * The part of an address a shipping rate or a tax rate depends on, or null
 * when it is not complete enough to quote (no zip or no country).
 */
export function normalizeQuoteDestination(location) {
  if (!location) return null;
  let country = clean(location.country);
  if (US_NAMES.has(country)) country = "US";
  let zip = clean(location.zip_code).replace(/\s+/g, "");
  if (country === "US") {
    const digits = zip.replace(/[^0-9]/g, "");
    zip = digits.slice(0, 5);
  }
  if (!country || !zip) return null;
  return { country, region: clean(location.region), zip };
}

function destinationParts(location) {
  const dest = normalizeQuoteDestination(location);
  if (!dest) return null;
  return [dest.country, dest.region, dest.zip, location.isCompanyLocation ? 1 : 0];
}

/** Key for one smart-shipping quote request, or null if not quotable. */
export function shippingQuoteKey({
  storeId,
  location,
  totalWeight,
  carrier,
  shippingType,
  internationalShippingType,
}) {
  const dest = destinationParts(location);
  if (!dest) return null;
  return JSON.stringify([
    "ship",
    String(storeId ?? ""),
    ...dest,
    Math.round(Number(totalWeight) || 0),
    carrier ?? null,
    shippingType ?? null,
    internationalShippingType ?? null,
  ]);
}

/** Key for one sales-tax lookup, or null if not quotable. */
export function salesTaxQuoteKey({ storeId, total, location }) {
  const dest = destinationParts(location);
  if (!dest) return null;
  return JSON.stringify([
    "tax",
    String(storeId ?? ""),
    ...dest,
    Math.round((Number(total) || 0) * 100),
  ]);
}

/**
 * A single-slot memo: the last successful answer and the fetch in flight.
 *
 * ``run(key, fetch, { cacheable })`` returns the stored answer when ``key``
 * matches it (and it is not too old), joins the fetch already in flight for
 * the same key, and otherwise calls ``fetch()``. An answer is stored only if
 * ``cacheable(answer)`` is true; an earlier fetch that lands after a newer
 * key was asked is returned to its own caller but not stored.
 */
export function createQuoteMemo({ maxAgeMs = QUOTE_MAX_AGE_MS, now = Date.now } = {}) {
  let stored = null; // { key, value, at }
  let inflight = null; // { key, promise }

  function fresh(key) {
    return Boolean(stored && stored.key === key && now() - stored.at <= maxAgeMs);
  }

  return {
    peek(key) {
      return fresh(key) ? { hit: true, value: stored.value } : { hit: false };
    },

    run(key, fetch, { cacheable = () => true } = {}) {
      if (fresh(key)) return Promise.resolve(stored.value);
      if (inflight && inflight.key === key) return inflight.promise;
      const entry = { key, promise: null };
      // `fetch` is started synchronously (a throw becomes a rejection).
      entry.promise = new Promise((resolve) => resolve(fetch()))
        .then(
          (value) => {
            if (inflight === entry) {
              inflight = null;
              if (cacheable(value)) stored = { key, value, at: now() };
            }
            return value;
          },
          (err) => {
            if (inflight === entry) inflight = null;
            throw err;
          }
        );
      inflight = entry;
      // A newer key supersedes whatever was stored for an older one.
      if (stored && stored.key !== key) stored = null;
      return entry.promise;
    },

    clear() {
      stored = null;
      inflight = null;
    },
  };
}

const memos = new WeakMap();

/**
 * The memo named ``name`` that belongs to ``owner`` (a component instance),
 * created on first use. Held outside the component's reactive data on
 * purpose: nothing renders from it, and making it reactive would only make
 * every lookup a render dependency.
 */
export function quoteMemoFor(owner, name) {
  let byName = memos.get(owner);
  if (!byName) {
    byName = new Map();
    memos.set(owner, byName);
  }
  let memo = byName.get(name);
  if (!memo) {
    memo = createQuoteMemo();
    byName.set(name, memo);
  }
  return memo;
}
